// Standard (non-Connect) Stripe config -- one platform-wide Stripe account,
// not one per tenant, matching how tenants are already billed to the
// company as a single line item (see SuperadminPricing.tsx). Only
// non-secret fields ever live in platform_billing_settings or pass through
// this screen: the Stripe SECRET key is never entered here, stored in the
// database, or handled by anyone else on your behalf -- set it as a
// Supabase Edge Function secret (Project Settings -> Edge Functions ->
// Secrets), the same place the TaxiCaller API key already lives.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Wallet, ShieldAlert, Save } from 'lucide-react';

interface BillingSettings {
  stripe_mode: 'test' | 'live';
  stripe_publishable_key: string | null;
  currency: string;
}

const SuperadminBilling: React.FC = () => {
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('platform_billing_settings')
        .select('stripe_mode, stripe_publishable_key, currency')
        .eq('id', true)
        .maybeSingle();
      if (error) {
        toast({ title: 'Error', description: 'Failed to load billing settings: ' + error.message, variant: 'destructive' });
      } else if (data) {
        setSettings(data as BillingSettings);
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('platform_billing_settings')
      .update({
        stripe_mode: settings.stripe_mode,
        stripe_publishable_key: settings.stripe_publishable_key,
        currency: settings.currency,
        updated_at: new Date().toISOString(),
        updated_by: userData.user?.id ?? null,
      })
      .eq('id', true);
    setSaving(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to save billing settings: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Saved', description: 'Billing settings updated.' });
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading billing settings...</p>;
  }
  if (!settings) {
    return <p className="text-sm text-muted-foreground">Billing settings unavailable.</p>;
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-foreground flex items-center gap-2">
            <Wallet className="h-4 w-4 text-yellow-500" />
            Stripe
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Standard Stripe billing -- one platform account, not Stripe Connect. Tenants are billed to the company, same as
            everywhere else in DMS.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-yellow-400 text-xs font-medium">Mode</Label>
              <Select
                value={settings.stripe_mode}
                onValueChange={(v) => setSettings({ ...settings, stripe_mode: v as 'test' | 'live' })}
              >
                <SelectTrigger className="dark-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black border-yellow-600">
                  <SelectItem value="test">Test</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-yellow-400 text-xs font-medium">Currency</Label>
              <Input
                className="dark-input uppercase"
                value={settings.currency}
                maxLength={3}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value.toLowerCase() })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-yellow-400 text-xs font-medium">Publishable key</Label>
            <Input
              className="dark-input font-mono text-xs"
              placeholder={settings.stripe_mode === 'live' ? 'pk_live_...' : 'pk_test_...'}
              value={settings.stripe_publishable_key ?? ''}
              onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
            />
            <p className="text-[11px] text-muted-foreground">
              Safe to store here -- Stripe's publishable key is meant to be public-facing.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="golden-button">
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-card border border-border">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-1.5">
              <p className="text-foreground font-medium">The Stripe secret key doesn't go here.</p>
              <p>
                Set it as a Supabase Edge Function secret instead (Project Settings → Edge Functions → Secrets, e.g.{' '}
                <code className="text-yellow-500">STRIPE_SECRET_KEY</code>) -- the same place the TaxiCaller API key already
                lives. It never gets typed into DMS or stored in the database, so nobody but you and Stripe ever see it.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminBilling;
